import { type GitWebhookMode } from '@apm/contracts';
import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { displayDateTime } from '../../../lib/date';
import { cn } from '../../../lib/utils';
import { type GitRepositoryCardSlotProps } from '../types';

/* Lớp viết sẵn — Tailwind không thấy lớp ghép lúc chạy. */
const DOT_CLASS: Readonly<Record<GitWebhookMode, string>> = {
  none: 'bg-border-strong',
  manual: 'bg-rag-green',
  auto: 'bg-rag-green',
};

/**
 * Tình trạng webhook của một kho, một dòng — APM-GIT-g,
 * docs/plan-git-integration.md §5.2. Dùng ở hai nơi: cắm vào thẻ kho của màn
 * Tổng quan (`GIT_REPOSITORY_CARD_SLOTS`) và đầu mỗi khối của mục cài đặt Webhook.
 *
 * Ba chế độ, ba câu: `none` nói thẳng kho **vẫn đang được kéo** theo chu kỳ —
 * không webhook không phải là hỏng; `manual` / `auto` kèm lần cuối một lượt gọi
 * **đúng bí mật** tới nơi, vì "đã bật" mà chưa từng nhận gì là dấu hiệu webhook ở
 * máy chủ git chưa trỏ đúng.
 *
 * ⚠️ Không nhận asOf (luật #7, ngoại lệ 1): thuần trình bày, và thứ nó vẽ là
 * trạng thái **vận hành lúc này** của kho — cùng loại với phần còn lại của thẻ kho.
 */
export function GitWebhookStatus({
  mode,
  lastSeenAt,
  className,
}: {
  readonly mode: GitWebhookMode;
  readonly lastSeenAt: string | null;
  readonly className?: string;
}): ReactNode {
  const { t } = useTranslation();
  return (
    <p
      data-testid="git-webhook-status"
      data-mode={mode}
      className={cn('flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-0.5 text-xs', className)}
    >
      <span aria-hidden className={cn('size-2 shrink-0 rounded-full', DOT_CLASS[mode])} />
      <span className="font-medium">{t(`gitTab.webhook.mode.${mode}`)}</span>
      {/* Dấu cách cho trình đọc màn hình — trong flex nó không chiếm chỗ. */}{' '}
      {mode === 'none' ? null : (
        <span className="text-muted-foreground">
          {lastSeenAt
            ? t('gitTab.webhook.lastSeen', { at: displayDateTime(lastSeenAt) })
            : t('gitTab.webhook.neverSeen')}
        </span>
      )}
    </p>
  );
}

/**
 * Chỗ cắm trên thẻ kho. `GitRepositoryView` đã mang đủ hai thứ cần
 * (`webhookMode`, `sync.webhookLastSeenAt`) nên không có lời gọi nào thêm — và
 * người chỉ có `git:read` cũng thấy được, dù không vào được màn cấu hình.
 *
 * ⚠️ Không nhận asOf: prop `asOf` của chỗ cắm bị bỏ qua có chủ ý — xem `GitWebhookStatus`.
 */
export function GitWebhookCardSlot({ repository }: GitRepositoryCardSlotProps): ReactNode {
  return (
    <GitWebhookStatus
      mode={repository.webhookMode}
      lastSeenAt={repository.sync?.webhookLastSeenAt ?? null}
    />
  );
}
